import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useT } from '../i18n';

const SUPABASE_FN = process.env.REACT_APP_SUPABASE_URL + '/functions/v1';

export interface LoyaltyReward {
  type: 'discount_percent' | 'menu_item';
  label: string | null;
  discount_percent: number | null;
  menu_item_id: number | null;
  items?: { menu_item_id: number; qty: number }[];
}

interface Props {
  restaurantId: string;
  reward: LoyaltyReward;
  menuItemName?: string | null;
  onClaimed: () => void;
  onClose: () => void;
}

async function callLoyaltyClaim(restaurantId: string) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(`${SUPABASE_FN}/loyalty-ops`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token ?? ''}` },
    body: JSON.stringify({ action: 'claim', restaurant_id: restaurantId }),
  });
  return res.json();
}

// A wrapped-gift shape, hand-drawn flat, in the same visual language as the wheel's own
// prize icon — replaces the plain 🎁 emoji this popup used to lean on.
function GiftIcon() {
  return (
    <svg viewBox="0 0 48 48" aria-hidden="true">
      <rect x="9" y="21" width="30" height="21" rx="2" fill="#8F3B4E" />
      <rect x="6" y="14.5" width="36" height="8" rx="2" fill="#6B2737" />
      <rect x="21.5" y="14.5" width="5" height="27.5" fill="#E3A94D" />
      <path d="M21 14.5 Q13 14.5 13 9 Q13 5 17.5 6 Q22 7.2 23 14.5 M27 14.5 Q35 14.5 35 9 Q35 5 30.5 6 Q26 7.2 25 14.5"
        fill="none" stroke="#6B2737" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// A simple checkmark badge for the claimed state — same mark used elsewhere for "done".
function CheckIcon() {
  return (
    <svg viewBox="0 0 48 48" aria-hidden="true">
      <circle cx="24" cy="24" r="20" fill="#8F3B4E" />
      <path d="M15 24.5 L21 30.5 L33.5 17" fill="none" stroke="#FFFFFF" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function LoyaltyPopupModal({ restaurantId, reward, menuItemName, onClaimed, onClose }: Props) {
  const t = useT();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState('');
  const [claimed, setClaimed] = useState(false);

  const rewardLabel = reward.type === 'menu_item'
    ? (menuItemName || reward.label || t.loyalty_reward_fallback)
    : `${reward.discount_percent ?? 0}% ${t.loyalty_popup_off}`;

  const claim = async () => {
    if (claiming) return;
    setClaiming(true);
    setError('');
    const result = await callLoyaltyClaim(restaurantId);
    setClaiming(false);
    if (result?.ok) {
      setClaimed(true);
      onClaimed();
    } else {
      setError(result?.error || t.loyalty_popup_error);
    }
  };

  // The reward is already live the moment it's claimed — no code, nothing to redeem later —
  // so the natural next step is straight to the menu, not a dead-end "close" button.
  const goOrder = () => {
    onClose();
    navigate(`/r/${slug}`);
  };

  return (
    <div className="wheel-popup-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="wheel-popup-box loyalty-popup-box">
        <button className="wheel-popup-close" onClick={onClose} aria-label="Close">✕</button>
        <div className="loyalty-popup-badge">
          {claimed ? <CheckIcon /> : <GiftIcon />}
        </div>
        <div className="wheel-popup-title">{claimed ? t.loyalty_popup_claimed_title : t.loyalty_popup_title}</div>
        <div className="loyalty-popup-reward">{rewardLabel}</div>

        {!claimed ? (
          <>
            {error && <div className="reg-err-box" style={{ marginTop: 10 }}>{error}</div>}
            <button className="loyalty-popup-claim-btn" onClick={claim} disabled={claiming}>
              {claiming ? t.loyalty_popup_claiming : t.loyalty_popup_claim_btn}
            </button>
          </>
        ) : (
          <>
            <p className="wheel-popup-done-hint">{t.loyalty_popup_claimed_hint}</p>
            <button className="loyalty-popup-claim-btn" onClick={goOrder}>{t.loyalty_popup_go_menu_btn}</button>
          </>
        )}
      </div>
    </div>
  );
}
